import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useMetrics, logger, useGetUserInformation } from 'appSelectorContext';
import { isEmpty } from 'lodash';

import { METRIC_PUBLISHER_NAMES } from '../../constants';
import { Context, Types } from '../../store';
import { ValidationStatus, ValidationStatusType } from '../../store/types';
import { useService } from '../../store/useService';
import { VALIDATE_CONTAINER } from './ScanContainerService';
import { CONTAINERS_ERROR_CODES, CONTAINER_ID_NOT_FOUND_METRIC } from './constants';

export const useScanContainer = (containerId?: string, onCompleted?: (val: string) => void, edit: boolean) => {
  const { dispatch }: any = useContext(Context);
  let { modelId }: any = useParams();
  const { useLazyQuery } = useService();
  const [error, setError] = useState<undefined | string>();
  const context = useMetrics();
  const userContext = useGetUserInformation();
  const publisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(
    METRIC_PUBLISHER_NAMES.SCAN_CONTAINER,
  );
  const reset = () => {
    setError(undefined);
  };

  useEffect(() => {
    setError(undefined);
  }, [containerId]);

  const [validateContainer, { loading, variables }] = useLazyQuery(VALIDATE_CONTAINER, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const scannedContainer = data?.validateContainerMetadata?.containerValidationList?.[0];
      const scannableContainerId = scannedContainer?.scannableContainerId || variables?.scannableContainerId;
      const status: ValidationStatus = scannedContainer?.status;

      const cmsSiteLaunched: Boolean = data?.validateContainerMetadata?.cmsSiteLaunched || false;

      if (
        cmsSiteLaunched &&
        (!status?.validationStatusType || status?.validationStatusType === ValidationStatusType.ERROR)
      ) {
        publisher.publishCounterMonitor(CONTAINER_ID_NOT_FOUND_METRIC, 1);
        setError(status?.statusCode || 'container_id_not_found');
        return;
      }

      if (
        !cmsSiteLaunched &&
        status?.validationStatusType === ValidationStatusType.ERROR &&
        status?.statusCode === CONTAINERS_ERROR_CODES.CONTAINER_MANAGEMENT_SERVICE_NOT_AVAILABLE
      ) {
        setError(status?.statusCode);
        return;
      }

      let containerType = scannedContainer?.containerType;
      // This could have an extra AND condition to only allow this for Container.TOTE. For safety I am assuming that
      // this could happen for any type and falling back to the container type being present.
      if (!cmsSiteLaunched && isEmpty(containerType)) {
        containerType = variables?.containerType;
      }

      if (edit) {
        logger.info(
          `editing a container after validation request: ${JSON.stringify({
            payload: {
              modelId,
              containerId,
              newContainerId: scannableContainerId,
              containerType: containerType,
              cmsSiteLaunched: cmsSiteLaunched,
            },
            withUser: userContext.user,
          })} `,
        );
        // editing a container
        dispatch({
          type: Types.moveContainer,
          payload: {
            modelId,
            containerId,
            newContainerId: scannableContainerId,
            containerType: containerType,
            cmsSiteLaunched: cmsSiteLaunched,
          },
        });
      } else {
        logger.info(
          `scanning a new container after validation request: ${JSON.stringify({
            payload: {
              modelId,
              containerId,
              newContainerId: scannableContainerId,
              containerType: scannedContainer?.containerType,
              cmsSiteLaunched: cmsSiteLaunched,
            },
            withUser: userContext.user,
          })} `,
        );
        dispatch({
          type: Types.scanContainer,
          payload: {
            modelId,
            containerId: scannableContainerId,
            containerType: scannedContainer?.containerType,
            cmsSiteLaunched: cmsSiteLaunched,
          },
        });
      }
      onCompleted && onCompleted(scannableContainerId);
    },
    onError: () => {
      setError('network_error');
    },
  });

  return {
    validateContainer,
    request: variables,
    loading,
    error,
    reset,
  };
};

export default useScanContainer;
